import * as z from 'zod';

export const CreateExperienceRequest = z.object({
  experience: z
    .object({
      _id: z
        .string()
        .describe('Experience ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      reservationLocationId: z
        .string()
        .describe(
          'ID of the reservation location this experience is assigned to.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the experience was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the experience was last updated.')
        .optional()
        .nullable(),
      configuration: z
        .object({
          displayInfo: z
            .object({
              name: z.string().describe('Experience name.').min(1).max(120),
              shortDescription: z
                .string()
                .describe('Brief description of the experience.')
                .max(300)
                .optional()
                .nullable(),
              coverImage: z
                .string()
                .describe('Cover image for the experience.')
                .optional(),
              richContent: z
                .any()
                .describe(
                  'Detailed description of the experience using rich content formatting.'
                )
                .optional(),
            })
            .describe('Display information for the experience.'),
          paymentPolicy: z
            .intersection(
              z.object({ paymentPolicyType: z.enum(['PER_GUEST', 'FREE']) }),
              z.xor([
                z.object({ perGuestOptions: z.never().optional() }),
                z.object({
                  perGuestOptions: z
                    .object({
                      price: z
                        .string()
                        .describe(
                          'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Per-guest pricing settings.'),
                }),
              ])
            )
            .describe(
              'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
            ),
          onlineReservations: z
            .object({
              partySize: z
                .object({
                  min: z
                    .number()
                    .int()
                    .describe(
                      'Minimum number of guests allowed for a reservation.'
                    )
                    .min(1)
                    .max(10000),
                  max: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of guests allowed for a reservation.'
                    )
                    .min(1)
                    .max(10000),
                })
                .describe('Party size.'),
              minimumReservationNotice: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                    )
                    .min(1)
                    .max(1000),
                  unit: z.enum(['MINUTES', 'HOURS', 'DAYS']),
                })
                .describe('Minimum reservation notice.')
                .optional(),
              maximumReservationNotice: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                    )
                    .min(1)
                    .max(1000),
                  unit: z.enum(['MINUTES', 'HOURS', 'DAYS']),
                })
                .describe('Maximum reservation notice.')
                .optional(),
              approval: z
                .object({ mode: z.enum(['AUTOMATIC', 'MANUAL']) })
                .describe('Approval.'),
              maxGuests: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Maximum total number of guests that can have active reservations for this experience at the same time.'
                    )
                    .min(0)
                    .max(10000),
                })
                .describe('Maximum number of guests.')
                .optional(),
              businessSchedule: z
                .object({
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe('Duration of the experience in minutes.')
                    .min(1)
                    .max(1440)
                    .optional()
                    .nullable(),
                  entries: z
                    .array(
                      z.intersection(
                        z.object({
                          recurrence: z.enum(['WEEKLY', 'ONE_TIME']).optional(),
                        }),
                        z.xor([
                          z.object({
                            weeklyOptions: z.never().optional(),
                            oneTimeOptions: z.never().optional(),
                          }),
                          z.object({
                            oneTimeOptions: z.never().optional(),
                            weeklyOptions: z
                              .object({
                                startDate: z
                                  .string()
                                  .describe('Start date.')
                                  .optional()
                                  .nullable(),
                                endDate: z
                                  .string()
                                  .describe('End date.')
                                  .optional()
                                  .nullable(),
                                startDaysAndTimes: z
                                  .array(
                                    z.object({
                                      day: z
                                        .enum([
                                          'MONDAY',
                                          'TUESDAY',
                                          'WEDNESDAY',
                                          'THURSDAY',
                                          'FRIDAY',
                                          'SATURDAY',
                                          'SUNDAY',
                                        ])
                                        .optional(),
                                      time: z
                                        .string()
                                        .describe(
                                          'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                        )
                                        .max(5)
                                        .min(5)
                                        .optional()
                                        .nullable(),
                                    })
                                  )
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Weekly recurrence settings.'),
                          }),
                          z.object({
                            weeklyOptions: z.never().optional(),
                            oneTimeOptions: z
                              .object({
                                startDate: z
                                  .string()
                                  .describe('Start date.')
                                  .optional()
                                  .nullable(),
                                startTime: z
                                  .string()
                                  .describe(
                                    'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                  )
                                  .max(5)
                                  .min(5)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('1-time event settings.'),
                          }),
                        ])
                      )
                    )
                    .max(10)
                    .optional(),
                })
                .describe('Business schedule.')
                .optional(),
            })
            .describe(
              'Settings for this experience that are used to determine restaurant availability for reservations made online.'
            ),
          reservationForm: z
            .object({
              submitMessage: z
                .string()
                .describe(
                  'Message displayed to customers when they submit the reservation form.'
                )
                .max(1000000)
                .optional()
                .nullable(),
              policiesEnabled: z
                .boolean()
                .describe(
                  'Whether to display privacy policy and terms and conditions to customers.'
                )
                .optional()
                .nullable(),
              privacyPolicy: z
                .intersection(
                  z.object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      url: z.never().optional(),
                      text: z.never().optional(),
                    }),
                    z.object({
                      text: z.never().optional(),
                      url: z.string().describe('Privacy policy URL.'),
                    }),
                    z.object({
                      url: z.never().optional(),
                      text: z
                        .string()
                        .describe('Privacy policy text.')
                        .max(1000000),
                    }),
                  ])
                )
                .describe('Privacy policy display settings.')
                .optional(),
              termsAndConditions: z
                .intersection(
                  z.object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      url: z.never().optional(),
                      text: z.never().optional(),
                    }),
                    z.object({
                      text: z.never().optional(),
                      url: z.string().describe('Terms and conditions URL.'),
                    }),
                    z.object({
                      url: z.never().optional(),
                      text: z
                        .string()
                        .describe('Terms and conditions text.')
                        .max(1000000),
                    }),
                  ])
                )
                .describe('Terms and conditions display settings.')
                .optional(),
              customFieldDefinitions: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Custom field ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Custom field name.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    required: z
                      .boolean()
                      .describe(
                        'Whether the custom field is required.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(1000)
                .optional(),
              lastNameRequired: z
                .boolean()
                .describe(
                  'Whether a last name is required in the reservation form.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              emailRequired: z
                .boolean()
                .describe(
                  'Whether an email is required in the reservation form.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              emailMarketingCheckbox: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                  checkedByDefault: z
                    .boolean()
                    .describe(
                      'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Email marketing checkbox settings.')
                .optional(),
            })
            .describe(
              'Custom form fields and settings for the reservation booking form.'
            )
            .optional(),
          visible: z
            .boolean()
            .describe('Whether the experience is visible on the live site.')
            .optional()
            .nullable(),
        })
        .describe('Experience configuration.'),
      currency: z
        .string()
        .describe(
          '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
        )
        .max(300)
        .optional()
        .nullable(),
      seoSettings: z
        .object({
          seoSchema: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe(
              'SEO schema data for customizing meta tags and search engine settings.'
            )
            .optional(),
        })
        .describe('SEO settings for the experience page.')
        .optional(),
    })
    .describe('Experience to create.'),
});
export const CreateExperienceResponse = z.object({
  _id: z
    .string()
    .describe('Experience ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  reservationLocationId: z
    .string()
    .describe('ID of the reservation location this experience is assigned to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the experience was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the experience was last updated.')
    .optional()
    .nullable(),
  configuration: z
    .object({
      displayInfo: z
        .object({
          name: z
            .string()
            .describe('Experience name.')
            .min(1)
            .max(120)
            .optional()
            .nullable(),
          shortDescription: z
            .string()
            .describe('Brief description of the experience.')
            .max(300)
            .optional()
            .nullable(),
          coverImage: z
            .string()
            .describe('Cover image for the experience.')
            .optional(),
          richContent: z
            .any()
            .describe(
              'Detailed description of the experience using rich content formatting.'
            )
            .optional(),
        })
        .describe('Display information for the experience.')
        .optional(),
      paymentPolicy: z
        .intersection(
          z.object({
            paymentPolicyType: z
              .enum(['PER_GUEST', 'FREE'])
              .describe('Payment policy type for the experience.')
              .optional(),
          }),
          z.xor([
            z.object({ perGuestOptions: z.never().optional() }),
            z.object({
              perGuestOptions: z
                .object({
                  price: z
                    .string()
                    .describe(
                      'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Per-guest pricing settings.'),
            }),
          ])
        )
        .describe(
          'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
        )
        .optional(),
      onlineReservations: z
        .object({
          partySize: z
            .object({
              min: z
                .number()
                .int()
                .describe('Minimum number of guests allowed for a reservation.')
                .min(1)
                .max(10000)
                .optional()
                .nullable(),
              max: z
                .number()
                .int()
                .describe('Maximum number of guests allowed for a reservation.')
                .min(1)
                .max(10000)
                .optional()
                .nullable(),
            })
            .describe('Party size.')
            .optional(),
          minimumReservationNotice: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              unit: z
                .enum(['MINUTES', 'HOURS', 'DAYS'])
                .describe('Time unit for the notice period.')
                .optional(),
            })
            .describe('Minimum reservation notice.')
            .optional(),
          maximumReservationNotice: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              unit: z
                .enum(['MINUTES', 'HOURS', 'DAYS'])
                .describe('Time unit for the notice period.')
                .optional(),
            })
            .describe('Maximum reservation notice.')
            .optional(),
          approval: z
            .object({
              mode: z
                .enum(['AUTOMATIC', 'MANUAL'])
                .describe('Approval mode.')
                .optional(),
            })
            .describe('Approval.')
            .optional(),
          maxGuests: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Maximum total number of guests that can have active reservations for this experience at the same time.'
                )
                .min(0)
                .max(10000)
                .optional()
                .nullable(),
            })
            .describe('Maximum number of guests.')
            .optional(),
          businessSchedule: z
            .object({
              durationInMinutes: z
                .number()
                .int()
                .describe('Duration of the experience in minutes.')
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              entries: z
                .array(
                  z.intersection(
                    z.object({
                      recurrence: z
                        .enum(['WEEKLY', 'ONE_TIME'])
                        .describe('Recurrence settings for this entry.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        weeklyOptions: z.never().optional(),
                        oneTimeOptions: z.never().optional(),
                      }),
                      z.object({
                        oneTimeOptions: z.never().optional(),
                        weeklyOptions: z
                          .object({
                            startDate: z
                              .string()
                              .describe('Start date.')
                              .optional()
                              .nullable(),
                            endDate: z
                              .string()
                              .describe('End date.')
                              .optional()
                              .nullable(),
                            startDaysAndTimes: z
                              .array(
                                z.object({
                                  day: z
                                    .enum([
                                      'MONDAY',
                                      'TUESDAY',
                                      'WEDNESDAY',
                                      'THURSDAY',
                                      'FRIDAY',
                                      'SATURDAY',
                                      'SUNDAY',
                                    ])
                                    .describe('Day of week.')
                                    .optional(),
                                  time: z
                                    .string()
                                    .describe(
                                      'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                    )
                                    .max(5)
                                    .min(5)
                                    .optional()
                                    .nullable(),
                                })
                              )
                              .max(100)
                              .optional(),
                          })
                          .describe('Weekly recurrence settings.'),
                      }),
                      z.object({
                        weeklyOptions: z.never().optional(),
                        oneTimeOptions: z
                          .object({
                            startDate: z
                              .string()
                              .describe('Start date.')
                              .optional()
                              .nullable(),
                            startTime: z
                              .string()
                              .describe(
                                'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                              )
                              .max(5)
                              .min(5)
                              .optional()
                              .nullable(),
                          })
                          .describe('1-time event settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
            })
            .describe('Business schedule.')
            .optional(),
        })
        .describe(
          'Settings for this experience that are used to determine restaurant availability for reservations made online.'
        )
        .optional(),
      reservationForm: z
        .object({
          submitMessage: z
            .string()
            .describe(
              'Message displayed to customers when they submit the reservation form.'
            )
            .max(1000000)
            .optional()
            .nullable(),
          policiesEnabled: z
            .boolean()
            .describe(
              'Whether to display privacy policy and terms and conditions to customers.'
            )
            .optional()
            .nullable(),
          privacyPolicy: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  url: z.never().optional(),
                  text: z.never().optional(),
                }),
                z.object({
                  text: z.never().optional(),
                  url: z.string().describe('Privacy policy URL.'),
                }),
                z.object({
                  url: z.never().optional(),
                  text: z
                    .string()
                    .describe('Privacy policy text.')
                    .max(1000000),
                }),
              ])
            )
            .describe('Privacy policy display settings.')
            .optional(),
          termsAndConditions: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  url: z.never().optional(),
                  text: z.never().optional(),
                }),
                z.object({
                  text: z.never().optional(),
                  url: z.string().describe('Terms and conditions URL.'),
                }),
                z.object({
                  url: z.never().optional(),
                  text: z
                    .string()
                    .describe('Terms and conditions text.')
                    .max(1000000),
                }),
              ])
            )
            .describe('Terms and conditions display settings.')
            .optional(),
          customFieldDefinitions: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Custom field ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Custom field name.')
                  .min(1)
                  .max(1000)
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the custom field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(1000)
            .optional(),
          lastNameRequired: z
            .boolean()
            .describe(
              'Whether a last name is required in the reservation form.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          emailRequired: z
            .boolean()
            .describe(
              'Whether an email is required in the reservation form.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          emailMarketingCheckbox: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              checkedByDefault: z
                .boolean()
                .describe(
                  'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe('Email marketing checkbox settings.')
            .optional(),
        })
        .describe(
          'Custom form fields and settings for the reservation booking form.'
        )
        .optional(),
      visible: z
        .boolean()
        .describe('Whether the experience is visible on the live site.')
        .optional()
        .nullable(),
    })
    .describe('Experience configuration.')
    .optional(),
  currency: z
    .string()
    .describe(
      '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
    )
    .optional(),
  slug: z
    .string()
    .describe(
      'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
    )
    .max(300)
    .optional()
    .nullable(),
  seoSettings: z
    .object({
      seoSchema: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe(
          'SEO schema data for customizing meta tags and search engine settings.'
        )
        .optional(),
    })
    .describe('SEO settings for the experience page.')
    .optional(),
});
export const GetExperienceRequest = z.object({
  experienceId: z
    .string()
    .describe('ID of the experience to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetExperienceResponse = z.object({
  _id: z
    .string()
    .describe('Experience ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  reservationLocationId: z
    .string()
    .describe('ID of the reservation location this experience is assigned to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the experience was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the experience was last updated.')
    .optional()
    .nullable(),
  configuration: z
    .object({
      displayInfo: z
        .object({
          name: z
            .string()
            .describe('Experience name.')
            .min(1)
            .max(120)
            .optional()
            .nullable(),
          shortDescription: z
            .string()
            .describe('Brief description of the experience.')
            .max(300)
            .optional()
            .nullable(),
          coverImage: z
            .string()
            .describe('Cover image for the experience.')
            .optional(),
          richContent: z
            .any()
            .describe(
              'Detailed description of the experience using rich content formatting.'
            )
            .optional(),
        })
        .describe('Display information for the experience.')
        .optional(),
      paymentPolicy: z
        .intersection(
          z.object({
            paymentPolicyType: z
              .enum(['PER_GUEST', 'FREE'])
              .describe('Payment policy type for the experience.')
              .optional(),
          }),
          z.xor([
            z.object({ perGuestOptions: z.never().optional() }),
            z.object({
              perGuestOptions: z
                .object({
                  price: z
                    .string()
                    .describe(
                      'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Per-guest pricing settings.'),
            }),
          ])
        )
        .describe(
          'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
        )
        .optional(),
      onlineReservations: z
        .object({
          partySize: z
            .object({
              min: z
                .number()
                .int()
                .describe('Minimum number of guests allowed for a reservation.')
                .min(1)
                .max(10000)
                .optional()
                .nullable(),
              max: z
                .number()
                .int()
                .describe('Maximum number of guests allowed for a reservation.')
                .min(1)
                .max(10000)
                .optional()
                .nullable(),
            })
            .describe('Party size.')
            .optional(),
          minimumReservationNotice: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              unit: z
                .enum(['MINUTES', 'HOURS', 'DAYS'])
                .describe('Time unit for the notice period.')
                .optional(),
            })
            .describe('Minimum reservation notice.')
            .optional(),
          maximumReservationNotice: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              unit: z
                .enum(['MINUTES', 'HOURS', 'DAYS'])
                .describe('Time unit for the notice period.')
                .optional(),
            })
            .describe('Maximum reservation notice.')
            .optional(),
          approval: z
            .object({
              mode: z
                .enum(['AUTOMATIC', 'MANUAL'])
                .describe('Approval mode.')
                .optional(),
            })
            .describe('Approval.')
            .optional(),
          maxGuests: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Maximum total number of guests that can have active reservations for this experience at the same time.'
                )
                .min(0)
                .max(10000)
                .optional()
                .nullable(),
            })
            .describe('Maximum number of guests.')
            .optional(),
          businessSchedule: z
            .object({
              durationInMinutes: z
                .number()
                .int()
                .describe('Duration of the experience in minutes.')
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              entries: z
                .array(
                  z.intersection(
                    z.object({
                      recurrence: z
                        .enum(['WEEKLY', 'ONE_TIME'])
                        .describe('Recurrence settings for this entry.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        weeklyOptions: z.never().optional(),
                        oneTimeOptions: z.never().optional(),
                      }),
                      z.object({
                        oneTimeOptions: z.never().optional(),
                        weeklyOptions: z
                          .object({
                            startDate: z
                              .string()
                              .describe('Start date.')
                              .optional()
                              .nullable(),
                            endDate: z
                              .string()
                              .describe('End date.')
                              .optional()
                              .nullable(),
                            startDaysAndTimes: z
                              .array(
                                z.object({
                                  day: z
                                    .enum([
                                      'MONDAY',
                                      'TUESDAY',
                                      'WEDNESDAY',
                                      'THURSDAY',
                                      'FRIDAY',
                                      'SATURDAY',
                                      'SUNDAY',
                                    ])
                                    .describe('Day of week.')
                                    .optional(),
                                  time: z
                                    .string()
                                    .describe(
                                      'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                    )
                                    .max(5)
                                    .min(5)
                                    .optional()
                                    .nullable(),
                                })
                              )
                              .max(100)
                              .optional(),
                          })
                          .describe('Weekly recurrence settings.'),
                      }),
                      z.object({
                        weeklyOptions: z.never().optional(),
                        oneTimeOptions: z
                          .object({
                            startDate: z
                              .string()
                              .describe('Start date.')
                              .optional()
                              .nullable(),
                            startTime: z
                              .string()
                              .describe(
                                'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                              )
                              .max(5)
                              .min(5)
                              .optional()
                              .nullable(),
                          })
                          .describe('1-time event settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
            })
            .describe('Business schedule.')
            .optional(),
        })
        .describe(
          'Settings for this experience that are used to determine restaurant availability for reservations made online.'
        )
        .optional(),
      reservationForm: z
        .object({
          submitMessage: z
            .string()
            .describe(
              'Message displayed to customers when they submit the reservation form.'
            )
            .max(1000000)
            .optional()
            .nullable(),
          policiesEnabled: z
            .boolean()
            .describe(
              'Whether to display privacy policy and terms and conditions to customers.'
            )
            .optional()
            .nullable(),
          privacyPolicy: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  url: z.never().optional(),
                  text: z.never().optional(),
                }),
                z.object({
                  text: z.never().optional(),
                  url: z.string().describe('Privacy policy URL.'),
                }),
                z.object({
                  url: z.never().optional(),
                  text: z
                    .string()
                    .describe('Privacy policy text.')
                    .max(1000000),
                }),
              ])
            )
            .describe('Privacy policy display settings.')
            .optional(),
          termsAndConditions: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  url: z.never().optional(),
                  text: z.never().optional(),
                }),
                z.object({
                  text: z.never().optional(),
                  url: z.string().describe('Terms and conditions URL.'),
                }),
                z.object({
                  url: z.never().optional(),
                  text: z
                    .string()
                    .describe('Terms and conditions text.')
                    .max(1000000),
                }),
              ])
            )
            .describe('Terms and conditions display settings.')
            .optional(),
          customFieldDefinitions: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Custom field ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Custom field name.')
                  .min(1)
                  .max(1000)
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the custom field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(1000)
            .optional(),
          lastNameRequired: z
            .boolean()
            .describe(
              'Whether a last name is required in the reservation form.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          emailRequired: z
            .boolean()
            .describe(
              'Whether an email is required in the reservation form.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          emailMarketingCheckbox: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              checkedByDefault: z
                .boolean()
                .describe(
                  'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe('Email marketing checkbox settings.')
            .optional(),
        })
        .describe(
          'Custom form fields and settings for the reservation booking form.'
        )
        .optional(),
      visible: z
        .boolean()
        .describe('Whether the experience is visible on the live site.')
        .optional()
        .nullable(),
    })
    .describe('Experience configuration.')
    .optional(),
  currency: z
    .string()
    .describe(
      '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
    )
    .optional(),
  slug: z
    .string()
    .describe(
      'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
    )
    .max(300)
    .optional()
    .nullable(),
  seoSettings: z
    .object({
      seoSchema: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe(
          'SEO schema data for customizing meta tags and search engine settings.'
        )
        .optional(),
    })
    .describe('SEO settings for the experience page.')
    .optional(),
});
export const GetExperienceBySlugRequest = z.object({
  slug: z.string().describe('Slug of the experience to retrieve.').max(200),
});
export const GetExperienceBySlugResponse = z.object({
  experience: z
    .object({
      _id: z
        .string()
        .describe('Experience ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      reservationLocationId: z
        .string()
        .describe(
          'ID of the reservation location this experience is assigned to.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the experience was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the experience was last updated.')
        .optional()
        .nullable(),
      configuration: z
        .object({
          displayInfo: z
            .object({
              name: z
                .string()
                .describe('Experience name.')
                .min(1)
                .max(120)
                .optional()
                .nullable(),
              shortDescription: z
                .string()
                .describe('Brief description of the experience.')
                .max(300)
                .optional()
                .nullable(),
              coverImage: z
                .string()
                .describe('Cover image for the experience.')
                .optional(),
              richContent: z
                .any()
                .describe(
                  'Detailed description of the experience using rich content formatting.'
                )
                .optional(),
            })
            .describe('Display information for the experience.')
            .optional(),
          paymentPolicy: z
            .intersection(
              z.object({
                paymentPolicyType: z
                  .enum(['PER_GUEST', 'FREE'])
                  .describe('Payment policy type for the experience.')
                  .optional(),
              }),
              z.xor([
                z.object({ perGuestOptions: z.never().optional() }),
                z.object({
                  perGuestOptions: z
                    .object({
                      price: z
                        .string()
                        .describe(
                          'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Per-guest pricing settings.'),
                }),
              ])
            )
            .describe(
              'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
            )
            .optional(),
          onlineReservations: z
            .object({
              partySize: z
                .object({
                  min: z
                    .number()
                    .int()
                    .describe(
                      'Minimum number of guests allowed for a reservation.'
                    )
                    .min(1)
                    .max(10000)
                    .optional()
                    .nullable(),
                  max: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of guests allowed for a reservation.'
                    )
                    .min(1)
                    .max(10000)
                    .optional()
                    .nullable(),
                })
                .describe('Party size.')
                .optional(),
              minimumReservationNotice: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                    )
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['MINUTES', 'HOURS', 'DAYS'])
                    .describe('Time unit for the notice period.')
                    .optional(),
                })
                .describe('Minimum reservation notice.')
                .optional(),
              maximumReservationNotice: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                    )
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['MINUTES', 'HOURS', 'DAYS'])
                    .describe('Time unit for the notice period.')
                    .optional(),
                })
                .describe('Maximum reservation notice.')
                .optional(),
              approval: z
                .object({
                  mode: z
                    .enum(['AUTOMATIC', 'MANUAL'])
                    .describe('Approval mode.')
                    .optional(),
                })
                .describe('Approval.')
                .optional(),
              maxGuests: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Maximum total number of guests that can have active reservations for this experience at the same time.'
                    )
                    .min(0)
                    .max(10000)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum number of guests.')
                .optional(),
              businessSchedule: z
                .object({
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe('Duration of the experience in minutes.')
                    .min(1)
                    .max(1440)
                    .optional()
                    .nullable(),
                  entries: z
                    .array(
                      z.intersection(
                        z.object({
                          recurrence: z
                            .enum(['WEEKLY', 'ONE_TIME'])
                            .describe('Recurrence settings for this entry.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            weeklyOptions: z.never().optional(),
                            oneTimeOptions: z.never().optional(),
                          }),
                          z.object({
                            oneTimeOptions: z.never().optional(),
                            weeklyOptions: z
                              .object({
                                startDate: z
                                  .string()
                                  .describe('Start date.')
                                  .optional()
                                  .nullable(),
                                endDate: z
                                  .string()
                                  .describe('End date.')
                                  .optional()
                                  .nullable(),
                                startDaysAndTimes: z
                                  .array(
                                    z.object({
                                      day: z
                                        .enum([
                                          'MONDAY',
                                          'TUESDAY',
                                          'WEDNESDAY',
                                          'THURSDAY',
                                          'FRIDAY',
                                          'SATURDAY',
                                          'SUNDAY',
                                        ])
                                        .describe('Day of week.')
                                        .optional(),
                                      time: z
                                        .string()
                                        .describe(
                                          'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                        )
                                        .max(5)
                                        .min(5)
                                        .optional()
                                        .nullable(),
                                    })
                                  )
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Weekly recurrence settings.'),
                          }),
                          z.object({
                            weeklyOptions: z.never().optional(),
                            oneTimeOptions: z
                              .object({
                                startDate: z
                                  .string()
                                  .describe('Start date.')
                                  .optional()
                                  .nullable(),
                                startTime: z
                                  .string()
                                  .describe(
                                    'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                  )
                                  .max(5)
                                  .min(5)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('1-time event settings.'),
                          }),
                        ])
                      )
                    )
                    .max(10)
                    .optional(),
                })
                .describe('Business schedule.')
                .optional(),
            })
            .describe(
              'Settings for this experience that are used to determine restaurant availability for reservations made online.'
            )
            .optional(),
          reservationForm: z
            .object({
              submitMessage: z
                .string()
                .describe(
                  'Message displayed to customers when they submit the reservation form.'
                )
                .max(1000000)
                .optional()
                .nullable(),
              policiesEnabled: z
                .boolean()
                .describe(
                  'Whether to display privacy policy and terms and conditions to customers.'
                )
                .optional()
                .nullable(),
              privacyPolicy: z
                .intersection(
                  z.object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      url: z.never().optional(),
                      text: z.never().optional(),
                    }),
                    z.object({
                      text: z.never().optional(),
                      url: z.string().describe('Privacy policy URL.'),
                    }),
                    z.object({
                      url: z.never().optional(),
                      text: z
                        .string()
                        .describe('Privacy policy text.')
                        .max(1000000),
                    }),
                  ])
                )
                .describe('Privacy policy display settings.')
                .optional(),
              termsAndConditions: z
                .intersection(
                  z.object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      url: z.never().optional(),
                      text: z.never().optional(),
                    }),
                    z.object({
                      text: z.never().optional(),
                      url: z.string().describe('Terms and conditions URL.'),
                    }),
                    z.object({
                      url: z.never().optional(),
                      text: z
                        .string()
                        .describe('Terms and conditions text.')
                        .max(1000000),
                    }),
                  ])
                )
                .describe('Terms and conditions display settings.')
                .optional(),
              customFieldDefinitions: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Custom field ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Custom field name.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    required: z
                      .boolean()
                      .describe(
                        'Whether the custom field is required.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(1000)
                .optional(),
              lastNameRequired: z
                .boolean()
                .describe(
                  'Whether a last name is required in the reservation form.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              emailRequired: z
                .boolean()
                .describe(
                  'Whether an email is required in the reservation form.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              emailMarketingCheckbox: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                  checkedByDefault: z
                    .boolean()
                    .describe(
                      'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Email marketing checkbox settings.')
                .optional(),
            })
            .describe(
              'Custom form fields and settings for the reservation booking form.'
            )
            .optional(),
          visible: z
            .boolean()
            .describe('Whether the experience is visible on the live site.')
            .optional()
            .nullable(),
        })
        .describe('Experience configuration.')
        .optional(),
      currency: z
        .string()
        .describe(
          '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
        )
        .max(300)
        .optional()
        .nullable(),
      seoSettings: z
        .object({
          seoSchema: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe(
              'SEO schema data for customizing meta tags and search engine settings.'
            )
            .optional(),
        })
        .describe('SEO settings for the experience page.')
        .optional(),
    })
    .describe('Retrieved experience.')
    .optional(),
});
export const UpdateExperienceRequest = z.object({
  _id: z
    .string()
    .describe('Experience ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  experience: z
    .object({
      _id: z
        .string()
        .describe('Experience ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      reservationLocationId: z
        .string()
        .describe(
          'ID of the reservation location this experience is assigned to.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the experience was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the experience was last updated.')
        .optional()
        .nullable(),
      configuration: z
        .object({
          displayInfo: z
            .object({
              name: z
                .string()
                .describe('Experience name.')
                .min(1)
                .max(120)
                .optional()
                .nullable(),
              shortDescription: z
                .string()
                .describe('Brief description of the experience.')
                .max(300)
                .optional()
                .nullable(),
              coverImage: z
                .string()
                .describe('Cover image for the experience.')
                .optional(),
              richContent: z
                .any()
                .describe(
                  'Detailed description of the experience using rich content formatting.'
                )
                .optional(),
            })
            .describe('Display information for the experience.')
            .optional(),
          paymentPolicy: z
            .intersection(
              z.object({
                paymentPolicyType: z.enum(['PER_GUEST', 'FREE']).optional(),
              }),
              z.xor([
                z.object({ perGuestOptions: z.never().optional() }),
                z.object({
                  perGuestOptions: z
                    .object({
                      price: z
                        .string()
                        .describe(
                          'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Per-guest pricing settings.'),
                }),
              ])
            )
            .describe(
              'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
            )
            .optional(),
          onlineReservations: z
            .object({
              partySize: z
                .object({
                  min: z
                    .number()
                    .int()
                    .describe(
                      'Minimum number of guests allowed for a reservation.'
                    )
                    .min(1)
                    .max(10000)
                    .optional()
                    .nullable(),
                  max: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of guests allowed for a reservation.'
                    )
                    .min(1)
                    .max(10000)
                    .optional()
                    .nullable(),
                })
                .describe('Party size.')
                .optional(),
              minimumReservationNotice: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                    )
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  unit: z.enum(['MINUTES', 'HOURS', 'DAYS']).optional(),
                })
                .describe('Minimum reservation notice.')
                .optional(),
              maximumReservationNotice: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                    )
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  unit: z.enum(['MINUTES', 'HOURS', 'DAYS']).optional(),
                })
                .describe('Maximum reservation notice.')
                .optional(),
              approval: z
                .object({ mode: z.enum(['AUTOMATIC', 'MANUAL']).optional() })
                .describe('Approval.')
                .optional(),
              maxGuests: z
                .object({
                  number: z
                    .number()
                    .int()
                    .describe(
                      'Maximum total number of guests that can have active reservations for this experience at the same time.'
                    )
                    .min(0)
                    .max(10000)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum number of guests.')
                .optional(),
              businessSchedule: z
                .object({
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe('Duration of the experience in minutes.')
                    .min(1)
                    .max(1440)
                    .optional()
                    .nullable(),
                  entries: z
                    .array(
                      z.intersection(
                        z.object({
                          recurrence: z.enum(['WEEKLY', 'ONE_TIME']).optional(),
                        }),
                        z.xor([
                          z.object({
                            weeklyOptions: z.never().optional(),
                            oneTimeOptions: z.never().optional(),
                          }),
                          z.object({
                            oneTimeOptions: z.never().optional(),
                            weeklyOptions: z
                              .object({
                                startDate: z
                                  .string()
                                  .describe('Start date.')
                                  .optional()
                                  .nullable(),
                                endDate: z
                                  .string()
                                  .describe('End date.')
                                  .optional()
                                  .nullable(),
                                startDaysAndTimes: z
                                  .array(
                                    z.object({
                                      day: z
                                        .enum([
                                          'MONDAY',
                                          'TUESDAY',
                                          'WEDNESDAY',
                                          'THURSDAY',
                                          'FRIDAY',
                                          'SATURDAY',
                                          'SUNDAY',
                                        ])
                                        .optional(),
                                      time: z
                                        .string()
                                        .describe(
                                          'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                        )
                                        .max(5)
                                        .min(5)
                                        .optional()
                                        .nullable(),
                                    })
                                  )
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Weekly recurrence settings.'),
                          }),
                          z.object({
                            weeklyOptions: z.never().optional(),
                            oneTimeOptions: z
                              .object({
                                startDate: z
                                  .string()
                                  .describe('Start date.')
                                  .optional()
                                  .nullable(),
                                startTime: z
                                  .string()
                                  .describe(
                                    'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                  )
                                  .max(5)
                                  .min(5)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('1-time event settings.'),
                          }),
                        ])
                      )
                    )
                    .max(10)
                    .optional(),
                })
                .describe('Business schedule.')
                .optional(),
            })
            .describe(
              'Settings for this experience that are used to determine restaurant availability for reservations made online.'
            )
            .optional(),
          reservationForm: z
            .object({
              submitMessage: z
                .string()
                .describe(
                  'Message displayed to customers when they submit the reservation form.'
                )
                .max(1000000)
                .optional()
                .nullable(),
              policiesEnabled: z
                .boolean()
                .describe(
                  'Whether to display privacy policy and terms and conditions to customers.'
                )
                .optional()
                .nullable(),
              privacyPolicy: z
                .intersection(
                  z.object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      url: z.never().optional(),
                      text: z.never().optional(),
                    }),
                    z.object({
                      text: z.never().optional(),
                      url: z.string().describe('Privacy policy URL.'),
                    }),
                    z.object({
                      url: z.never().optional(),
                      text: z
                        .string()
                        .describe('Privacy policy text.')
                        .max(1000000),
                    }),
                  ])
                )
                .describe('Privacy policy display settings.')
                .optional(),
              termsAndConditions: z
                .intersection(
                  z.object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      url: z.never().optional(),
                      text: z.never().optional(),
                    }),
                    z.object({
                      text: z.never().optional(),
                      url: z.string().describe('Terms and conditions URL.'),
                    }),
                    z.object({
                      url: z.never().optional(),
                      text: z
                        .string()
                        .describe('Terms and conditions text.')
                        .max(1000000),
                    }),
                  ])
                )
                .describe('Terms and conditions display settings.')
                .optional(),
              customFieldDefinitions: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Custom field ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Custom field name.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    required: z
                      .boolean()
                      .describe(
                        'Whether the custom field is required.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(1000)
                .optional(),
              lastNameRequired: z
                .boolean()
                .describe(
                  'Whether a last name is required in the reservation form.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              emailRequired: z
                .boolean()
                .describe(
                  'Whether an email is required in the reservation form.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              emailMarketingCheckbox: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                  checkedByDefault: z
                    .boolean()
                    .describe(
                      'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Email marketing checkbox settings.')
                .optional(),
            })
            .describe(
              'Custom form fields and settings for the reservation booking form.'
            )
            .optional(),
          visible: z
            .boolean()
            .describe('Whether the experience is visible on the live site.')
            .optional()
            .nullable(),
        })
        .describe('Experience configuration.')
        .optional(),
      currency: z
        .string()
        .describe(
          '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
        )
        .max(300)
        .optional()
        .nullable(),
      seoSettings: z
        .object({
          seoSchema: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe(
              'SEO schema data for customizing meta tags and search engine settings.'
            )
            .optional(),
        })
        .describe('SEO settings for the experience page.')
        .optional(),
    })
    .describe('Experience to update. May be partial.'),
});
export const UpdateExperienceResponse = z.object({
  _id: z
    .string()
    .describe('Experience ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  reservationLocationId: z
    .string()
    .describe('ID of the reservation location this experience is assigned to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the experience was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the experience was last updated.')
    .optional()
    .nullable(),
  configuration: z
    .object({
      displayInfo: z
        .object({
          name: z
            .string()
            .describe('Experience name.')
            .min(1)
            .max(120)
            .optional()
            .nullable(),
          shortDescription: z
            .string()
            .describe('Brief description of the experience.')
            .max(300)
            .optional()
            .nullable(),
          coverImage: z
            .string()
            .describe('Cover image for the experience.')
            .optional(),
          richContent: z
            .any()
            .describe(
              'Detailed description of the experience using rich content formatting.'
            )
            .optional(),
        })
        .describe('Display information for the experience.')
        .optional(),
      paymentPolicy: z
        .intersection(
          z.object({
            paymentPolicyType: z
              .enum(['PER_GUEST', 'FREE'])
              .describe('Payment policy type for the experience.')
              .optional(),
          }),
          z.xor([
            z.object({ perGuestOptions: z.never().optional() }),
            z.object({
              perGuestOptions: z
                .object({
                  price: z
                    .string()
                    .describe(
                      'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Per-guest pricing settings.'),
            }),
          ])
        )
        .describe(
          'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
        )
        .optional(),
      onlineReservations: z
        .object({
          partySize: z
            .object({
              min: z
                .number()
                .int()
                .describe('Minimum number of guests allowed for a reservation.')
                .min(1)
                .max(10000)
                .optional()
                .nullable(),
              max: z
                .number()
                .int()
                .describe('Maximum number of guests allowed for a reservation.')
                .min(1)
                .max(10000)
                .optional()
                .nullable(),
            })
            .describe('Party size.')
            .optional(),
          minimumReservationNotice: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              unit: z
                .enum(['MINUTES', 'HOURS', 'DAYS'])
                .describe('Time unit for the notice period.')
                .optional(),
            })
            .describe('Minimum reservation notice.')
            .optional(),
          maximumReservationNotice: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              unit: z
                .enum(['MINUTES', 'HOURS', 'DAYS'])
                .describe('Time unit for the notice period.')
                .optional(),
            })
            .describe('Maximum reservation notice.')
            .optional(),
          approval: z
            .object({
              mode: z
                .enum(['AUTOMATIC', 'MANUAL'])
                .describe('Approval mode.')
                .optional(),
            })
            .describe('Approval.')
            .optional(),
          maxGuests: z
            .object({
              number: z
                .number()
                .int()
                .describe(
                  'Maximum total number of guests that can have active reservations for this experience at the same time.'
                )
                .min(0)
                .max(10000)
                .optional()
                .nullable(),
            })
            .describe('Maximum number of guests.')
            .optional(),
          businessSchedule: z
            .object({
              durationInMinutes: z
                .number()
                .int()
                .describe('Duration of the experience in minutes.')
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              entries: z
                .array(
                  z.intersection(
                    z.object({
                      recurrence: z
                        .enum(['WEEKLY', 'ONE_TIME'])
                        .describe('Recurrence settings for this entry.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        weeklyOptions: z.never().optional(),
                        oneTimeOptions: z.never().optional(),
                      }),
                      z.object({
                        oneTimeOptions: z.never().optional(),
                        weeklyOptions: z
                          .object({
                            startDate: z
                              .string()
                              .describe('Start date.')
                              .optional()
                              .nullable(),
                            endDate: z
                              .string()
                              .describe('End date.')
                              .optional()
                              .nullable(),
                            startDaysAndTimes: z
                              .array(
                                z.object({
                                  day: z
                                    .enum([
                                      'MONDAY',
                                      'TUESDAY',
                                      'WEDNESDAY',
                                      'THURSDAY',
                                      'FRIDAY',
                                      'SATURDAY',
                                      'SUNDAY',
                                    ])
                                    .describe('Day of week.')
                                    .optional(),
                                  time: z
                                    .string()
                                    .describe(
                                      'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                    )
                                    .max(5)
                                    .min(5)
                                    .optional()
                                    .nullable(),
                                })
                              )
                              .max(100)
                              .optional(),
                          })
                          .describe('Weekly recurrence settings.'),
                      }),
                      z.object({
                        weeklyOptions: z.never().optional(),
                        oneTimeOptions: z
                          .object({
                            startDate: z
                              .string()
                              .describe('Start date.')
                              .optional()
                              .nullable(),
                            startTime: z
                              .string()
                              .describe(
                                'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                              )
                              .max(5)
                              .min(5)
                              .optional()
                              .nullable(),
                          })
                          .describe('1-time event settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
            })
            .describe('Business schedule.')
            .optional(),
        })
        .describe(
          'Settings for this experience that are used to determine restaurant availability for reservations made online.'
        )
        .optional(),
      reservationForm: z
        .object({
          submitMessage: z
            .string()
            .describe(
              'Message displayed to customers when they submit the reservation form.'
            )
            .max(1000000)
            .optional()
            .nullable(),
          policiesEnabled: z
            .boolean()
            .describe(
              'Whether to display privacy policy and terms and conditions to customers.'
            )
            .optional()
            .nullable(),
          privacyPolicy: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  url: z.never().optional(),
                  text: z.never().optional(),
                }),
                z.object({
                  text: z.never().optional(),
                  url: z.string().describe('Privacy policy URL.'),
                }),
                z.object({
                  url: z.never().optional(),
                  text: z
                    .string()
                    .describe('Privacy policy text.')
                    .max(1000000),
                }),
              ])
            )
            .describe('Privacy policy display settings.')
            .optional(),
          termsAndConditions: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  url: z.never().optional(),
                  text: z.never().optional(),
                }),
                z.object({
                  text: z.never().optional(),
                  url: z.string().describe('Terms and conditions URL.'),
                }),
                z.object({
                  url: z.never().optional(),
                  text: z
                    .string()
                    .describe('Terms and conditions text.')
                    .max(1000000),
                }),
              ])
            )
            .describe('Terms and conditions display settings.')
            .optional(),
          customFieldDefinitions: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Custom field ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Custom field name.')
                  .min(1)
                  .max(1000)
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the custom field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(1000)
            .optional(),
          lastNameRequired: z
            .boolean()
            .describe(
              'Whether a last name is required in the reservation form.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          emailRequired: z
            .boolean()
            .describe(
              'Whether an email is required in the reservation form.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          emailMarketingCheckbox: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
              checkedByDefault: z
                .boolean()
                .describe(
                  'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe('Email marketing checkbox settings.')
            .optional(),
        })
        .describe(
          'Custom form fields and settings for the reservation booking form.'
        )
        .optional(),
      visible: z
        .boolean()
        .describe('Whether the experience is visible on the live site.')
        .optional()
        .nullable(),
    })
    .describe('Experience configuration.')
    .optional(),
  currency: z
    .string()
    .describe(
      '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
    )
    .optional(),
  slug: z
    .string()
    .describe(
      'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
    )
    .max(300)
    .optional()
    .nullable(),
  seoSettings: z
    .object({
      seoSchema: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe(
          'SEO schema data for customizing meta tags and search engine settings.'
        )
        .optional(),
    })
    .describe('SEO settings for the experience page.')
    .optional(),
});
export const QueryExperiencesRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Query criteria for filtering and sorting experiences.'),
});
export const QueryExperiencesResponse = z.object({
  experiences: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Experience ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        reservationLocationId: z
          .string()
          .describe(
            'ID of the reservation location this experience is assigned to.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the experience was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the experience was last updated.')
          .optional()
          .nullable(),
        configuration: z
          .object({
            displayInfo: z
              .object({
                name: z
                  .string()
                  .describe('Experience name.')
                  .min(1)
                  .max(120)
                  .optional()
                  .nullable(),
                shortDescription: z
                  .string()
                  .describe('Brief description of the experience.')
                  .max(300)
                  .optional()
                  .nullable(),
                coverImage: z
                  .string()
                  .describe('Cover image for the experience.')
                  .optional(),
                richContent: z
                  .any()
                  .describe(
                    'Detailed description of the experience using rich content formatting.'
                  )
                  .optional(),
              })
              .describe('Display information for the experience.')
              .optional(),
            paymentPolicy: z
              .intersection(
                z.object({
                  paymentPolicyType: z
                    .enum(['PER_GUEST', 'FREE'])
                    .describe('Payment policy type for the experience.')
                    .optional(),
                }),
                z.xor([
                  z.object({ perGuestOptions: z.never().optional() }),
                  z.object({
                    perGuestOptions: z
                      .object({
                        price: z
                          .string()
                          .describe(
                            'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Per-guest pricing settings.'),
                  }),
                ])
              )
              .describe(
                'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
              )
              .optional(),
            onlineReservations: z
              .object({
                partySize: z
                  .object({
                    min: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of guests allowed for a reservation.'
                      )
                      .min(1)
                      .max(10000)
                      .optional()
                      .nullable(),
                    max: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of guests allowed for a reservation.'
                      )
                      .min(1)
                      .max(10000)
                      .optional()
                      .nullable(),
                  })
                  .describe('Party size.')
                  .optional(),
                minimumReservationNotice: z
                  .object({
                    number: z
                      .number()
                      .int()
                      .describe(
                        'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                      .describe('Time unit for the notice period.')
                      .optional(),
                  })
                  .describe('Minimum reservation notice.')
                  .optional(),
                maximumReservationNotice: z
                  .object({
                    number: z
                      .number()
                      .int()
                      .describe(
                        'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                      .describe('Time unit for the notice period.')
                      .optional(),
                  })
                  .describe('Maximum reservation notice.')
                  .optional(),
                approval: z
                  .object({
                    mode: z
                      .enum(['AUTOMATIC', 'MANUAL'])
                      .describe('Approval mode.')
                      .optional(),
                  })
                  .describe('Approval.')
                  .optional(),
                maxGuests: z
                  .object({
                    number: z
                      .number()
                      .int()
                      .describe(
                        'Maximum total number of guests that can have active reservations for this experience at the same time.'
                      )
                      .min(0)
                      .max(10000)
                      .optional()
                      .nullable(),
                  })
                  .describe('Maximum number of guests.')
                  .optional(),
                businessSchedule: z
                  .object({
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe('Duration of the experience in minutes.')
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    entries: z
                      .array(
                        z.intersection(
                          z.object({
                            recurrence: z
                              .enum(['WEEKLY', 'ONE_TIME'])
                              .describe('Recurrence settings for this entry.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              weeklyOptions: z.never().optional(),
                              oneTimeOptions: z.never().optional(),
                            }),
                            z.object({
                              oneTimeOptions: z.never().optional(),
                              weeklyOptions: z
                                .object({
                                  startDate: z
                                    .string()
                                    .describe('Start date.')
                                    .optional()
                                    .nullable(),
                                  endDate: z
                                    .string()
                                    .describe('End date.')
                                    .optional()
                                    .nullable(),
                                  startDaysAndTimes: z
                                    .array(
                                      z.object({
                                        day: z
                                          .enum([
                                            'MONDAY',
                                            'TUESDAY',
                                            'WEDNESDAY',
                                            'THURSDAY',
                                            'FRIDAY',
                                            'SATURDAY',
                                            'SUNDAY',
                                          ])
                                          .describe('Day of week.')
                                          .optional(),
                                        time: z
                                          .string()
                                          .describe(
                                            'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                          )
                                          .max(5)
                                          .min(5)
                                          .optional()
                                          .nullable(),
                                      })
                                    )
                                    .max(100)
                                    .optional(),
                                })
                                .describe('Weekly recurrence settings.'),
                            }),
                            z.object({
                              weeklyOptions: z.never().optional(),
                              oneTimeOptions: z
                                .object({
                                  startDate: z
                                    .string()
                                    .describe('Start date.')
                                    .optional()
                                    .nullable(),
                                  startTime: z
                                    .string()
                                    .describe(
                                      'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                    )
                                    .max(5)
                                    .min(5)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('1-time event settings.'),
                            }),
                          ])
                        )
                      )
                      .max(10)
                      .optional(),
                  })
                  .describe('Business schedule.')
                  .optional(),
              })
              .describe(
                'Settings for this experience that are used to determine restaurant availability for reservations made online.'
              )
              .optional(),
            reservationForm: z
              .object({
                submitMessage: z
                  .string()
                  .describe(
                    'Message displayed to customers when they submit the reservation form.'
                  )
                  .max(1000000)
                  .optional()
                  .nullable(),
                policiesEnabled: z
                  .boolean()
                  .describe(
                    'Whether to display privacy policy and terms and conditions to customers.'
                  )
                  .optional()
                  .nullable(),
                privacyPolicy: z
                  .intersection(
                    z.object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        url: z.never().optional(),
                        text: z.never().optional(),
                      }),
                      z.object({
                        text: z.never().optional(),
                        url: z.string().describe('Privacy policy URL.'),
                      }),
                      z.object({
                        url: z.never().optional(),
                        text: z
                          .string()
                          .describe('Privacy policy text.')
                          .max(1000000),
                      }),
                    ])
                  )
                  .describe('Privacy policy display settings.')
                  .optional(),
                termsAndConditions: z
                  .intersection(
                    z.object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        url: z.never().optional(),
                        text: z.never().optional(),
                      }),
                      z.object({
                        text: z.never().optional(),
                        url: z.string().describe('Terms and conditions URL.'),
                      }),
                      z.object({
                        url: z.never().optional(),
                        text: z
                          .string()
                          .describe('Terms and conditions text.')
                          .max(1000000),
                      }),
                    ])
                  )
                  .describe('Terms and conditions display settings.')
                  .optional(),
                customFieldDefinitions: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Custom field ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Custom field name.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                      required: z
                        .boolean()
                        .describe(
                          'Whether the custom field is required.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(1000)
                  .optional(),
                lastNameRequired: z
                  .boolean()
                  .describe(
                    'Whether a last name is required in the reservation form.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                emailRequired: z
                  .boolean()
                  .describe(
                    'Whether an email is required in the reservation form.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                emailMarketingCheckbox: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                    checkedByDefault: z
                      .boolean()
                      .describe(
                        'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Email marketing checkbox settings.')
                  .optional(),
              })
              .describe(
                'Custom form fields and settings for the reservation booking form.'
              )
              .optional(),
            visible: z
              .boolean()
              .describe('Whether the experience is visible on the live site.')
              .optional()
              .nullable(),
          })
          .describe('Experience configuration.')
          .optional(),
        currency: z
          .string()
          .describe(
            '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
          )
          .optional(),
        slug: z
          .string()
          .describe(
            'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
          )
          .max(300)
          .optional()
          .nullable(),
        seoSettings: z
          .object({
            seoSchema: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe(
                'SEO schema data for customizing meta tags and search engine settings.'
              )
              .optional(),
          })
          .describe('SEO settings for the experience page.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Pagination information for the query results.')
    .optional(),
});
export const SearchExperiencesRequest = z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(100)
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z.string().max(1000).optional().nullable(),
                type: z
                  .enum([
                    'UNKNOWN_AGGREGATION_TYPE',
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                    'DATE_HISTOGRAM',
                    'NESTED',
                  ])
                  .optional(),
                fieldPath: z.string().max(1000).optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  value: z
                    .intersection(
                      z.object({
                        sortType: z
                          .enum(['COUNT', 'VALUE'])
                          .describe(
                            'Whether to sort by the number of matches or the value of the field.'
                          )
                          .optional(),
                        sortDirection: z
                          .enum(['DESC', 'ASC'])
                          .describe(
                            'Whether to sort in ascending or descending order.'
                          )
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                          )
                          .optional()
                          .nullable(),
                        missingValues: z
                          .enum(['EXCLUDE', 'INCLUDE'])
                          .describe(
                            'Whether missing values should be included or excluded from the aggregation results.\nDefault: `EXCLUDE`.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ includeOptions: z.never().optional() }),
                        z.object({
                          includeOptions: z
                            .object({
                              addToBucket: z
                                .string()
                                .describe(
                                  'Specify a custom name for the bucket containing the missing values. Defaults are "N/A" for strings, "0" for integers, and "false" for booleans.'
                                )
                                .max(1000)
                                .optional(),
                            })
                            .describe(
                              'Options for including missing values in results.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'A value aggregation calculates metrics such as "count" for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number of reservations (count) of each status that have been made on the site.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if `to` is not provided.'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if `from` is not provided.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(1000)
                        .optional(),
                    })
                    .describe(
                      'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum([
                          'UNKNOWN_SCALAR_TYPE',
                          'COUNT_DISTINCT',
                          'MIN',
                          'MAX',
                        ])
                        .describe('Operation type for the scalar aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A scalar aggregation calculates a single numerical value from a dataset, such as the total sum, average, min, or max, summarizing the dataset into one key metric. For example, use a scalar aggregation to get the average part size of reservations made on a site.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  nested: z.never().optional(),
                  dateHistogram: z
                    .object({
                      interval: z
                        .enum([
                          'YEAR',
                          'MONTH',
                          'WEEK',
                          'DAY',
                          'HOUR',
                          'MINUTE',
                          'SECOND',
                        ])
                        .describe('Interval for date histogram aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z
                    .object({
                      nestedAggregations: z
                        .array(
                          z.intersection(
                            z.object({
                              name: z
                                .string()
                                .describe(
                                  'Aggregation name displayed in the return.'
                                )
                                .max(1000)
                                .optional()
                                .nullable(),
                              type: z
                                .enum([
                                  'UNKNOWN_AGGREGATION_TYPE',
                                  'VALUE',
                                  'RANGE',
                                  'SCALAR',
                                  'DATE_HISTOGRAM',
                                ])
                                .optional(),
                              fieldPath: z
                                .string()
                                .describe('Field to aggregate by.')
                                .max(1000)
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                              }),
                              z.object({
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                value: z
                                  .intersection(
                                    z.object({
                                      sortType: z
                                        .enum(['COUNT', 'VALUE'])
                                        .describe(
                                          'Whether to sort by the number of matches or the value of the field.'
                                        )
                                        .optional(),
                                      sortDirection: z
                                        .enum(['DESC', 'ASC'])
                                        .describe(
                                          'Whether to sort in ascending or descending order.'
                                        )
                                        .optional(),
                                      limit: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                        )
                                        .optional()
                                        .nullable(),
                                      missingValues: z
                                        .enum(['EXCLUDE', 'INCLUDE'])
                                        .describe(
                                          'Whether missing values should be included or excluded from the aggregation results.\nDefault: `EXCLUDE`.'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        includeOptions: z.never().optional(),
                                      }),
                                      z.object({
                                        includeOptions: z
                                          .object({
                                            addToBucket: z
                                              .string()
                                              .describe(
                                                'Specify a custom name for the bucket containing the missing values. Defaults are "N/A" for strings, "0" for integers, and "false" for booleans.'
                                              )
                                              .max(1000)
                                              .optional(),
                                          })
                                          .describe(
                                            'Options for including missing values in results.'
                                          ),
                                      }),
                                    ])
                                  )
                                  .describe(
                                    'A value aggregation calculates metrics such as "count" for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number of reservations (count) of each status that have been made on the site.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                range: z
                                  .object({
                                    buckets: z
                                      .array(
                                        z.object({
                                          from: z
                                            .number()
                                            .describe(
                                              'Inclusive lower bound of the range. Required if `to` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                          to: z
                                            .number()
                                            .describe(
                                              'Exclusive upper bound of the range. Required if `from` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                        })
                                      )
                                      .max(1000)
                                      .optional(),
                                  })
                                  .describe(
                                    'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                scalar: z
                                  .object({
                                    type: z
                                      .enum([
                                        'UNKNOWN_SCALAR_TYPE',
                                        'COUNT_DISTINCT',
                                        'MIN',
                                        'MAX',
                                      ])
                                      .describe(
                                        'Operation type for the scalar aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A scalar aggregation calculates a single numerical value from a dataset, such as the total sum, average, min, or max, summarizing the dataset into one key metric. For example, use a scalar aggregation to get the average part size of reservations made on a site.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z
                                  .object({
                                    interval: z
                                      .enum([
                                        'YEAR',
                                        'MONTH',
                                        'WEEK',
                                        'DAY',
                                        'HOUR',
                                        'MINUTE',
                                        'SECOND',
                                      ])
                                      .describe(
                                        'Interval for date histogram aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.'
                                  ),
                              }),
                            ])
                          )
                        )
                        .min(2)
                        .max(10)
                        .optional(),
                    })
                    .describe(
                      'A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy.\nFor example, to get the number of reservations by party size for each week, first perform a date histogram aggregation on `details.startDate` with the interval `WEEK`, and a second value aggregation on `details.partySize`.'
                    ),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression.')
              .max(100)
              .optional()
              .nullable(),
            fields: z.array(z.string()).max(100).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.\nApplies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".'
          )
          .max(100)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Search query.'),
});
export const SearchExperiencesResponse = z.object({
  experiences: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Experience ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        reservationLocationId: z
          .string()
          .describe(
            'ID of the reservation location this experience is assigned to.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the experience was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the experience was last updated.')
          .optional()
          .nullable(),
        configuration: z
          .object({
            displayInfo: z
              .object({
                name: z
                  .string()
                  .describe('Experience name.')
                  .min(1)
                  .max(120)
                  .optional()
                  .nullable(),
                shortDescription: z
                  .string()
                  .describe('Brief description of the experience.')
                  .max(300)
                  .optional()
                  .nullable(),
                coverImage: z
                  .string()
                  .describe('Cover image for the experience.')
                  .optional(),
                richContent: z
                  .any()
                  .describe(
                    'Detailed description of the experience using rich content formatting.'
                  )
                  .optional(),
              })
              .describe('Display information for the experience.')
              .optional(),
            paymentPolicy: z
              .intersection(
                z.object({
                  paymentPolicyType: z
                    .enum(['PER_GUEST', 'FREE'])
                    .describe('Payment policy type for the experience.')
                    .optional(),
                }),
                z.xor([
                  z.object({ perGuestOptions: z.never().optional() }),
                  z.object({
                    perGuestOptions: z
                      .object({
                        price: z
                          .string()
                          .describe(
                            'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Per-guest pricing settings.'),
                  }),
                ])
              )
              .describe(
                'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
              )
              .optional(),
            onlineReservations: z
              .object({
                partySize: z
                  .object({
                    min: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of guests allowed for a reservation.'
                      )
                      .min(1)
                      .max(10000)
                      .optional()
                      .nullable(),
                    max: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of guests allowed for a reservation.'
                      )
                      .min(1)
                      .max(10000)
                      .optional()
                      .nullable(),
                  })
                  .describe('Party size.')
                  .optional(),
                minimumReservationNotice: z
                  .object({
                    number: z
                      .number()
                      .int()
                      .describe(
                        'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                      .describe('Time unit for the notice period.')
                      .optional(),
                  })
                  .describe('Minimum reservation notice.')
                  .optional(),
                maximumReservationNotice: z
                  .object({
                    number: z
                      .number()
                      .int()
                      .describe(
                        'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                      .describe('Time unit for the notice period.')
                      .optional(),
                  })
                  .describe('Maximum reservation notice.')
                  .optional(),
                approval: z
                  .object({
                    mode: z
                      .enum(['AUTOMATIC', 'MANUAL'])
                      .describe('Approval mode.')
                      .optional(),
                  })
                  .describe('Approval.')
                  .optional(),
                maxGuests: z
                  .object({
                    number: z
                      .number()
                      .int()
                      .describe(
                        'Maximum total number of guests that can have active reservations for this experience at the same time.'
                      )
                      .min(0)
                      .max(10000)
                      .optional()
                      .nullable(),
                  })
                  .describe('Maximum number of guests.')
                  .optional(),
                businessSchedule: z
                  .object({
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe('Duration of the experience in minutes.')
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    entries: z
                      .array(
                        z.intersection(
                          z.object({
                            recurrence: z
                              .enum(['WEEKLY', 'ONE_TIME'])
                              .describe('Recurrence settings for this entry.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              weeklyOptions: z.never().optional(),
                              oneTimeOptions: z.never().optional(),
                            }),
                            z.object({
                              oneTimeOptions: z.never().optional(),
                              weeklyOptions: z
                                .object({
                                  startDate: z
                                    .string()
                                    .describe('Start date.')
                                    .optional()
                                    .nullable(),
                                  endDate: z
                                    .string()
                                    .describe('End date.')
                                    .optional()
                                    .nullable(),
                                  startDaysAndTimes: z
                                    .array(
                                      z.object({
                                        day: z
                                          .enum([
                                            'MONDAY',
                                            'TUESDAY',
                                            'WEDNESDAY',
                                            'THURSDAY',
                                            'FRIDAY',
                                            'SATURDAY',
                                            'SUNDAY',
                                          ])
                                          .describe('Day of week.')
                                          .optional(),
                                        time: z
                                          .string()
                                          .describe(
                                            'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                          )
                                          .max(5)
                                          .min(5)
                                          .optional()
                                          .nullable(),
                                      })
                                    )
                                    .max(100)
                                    .optional(),
                                })
                                .describe('Weekly recurrence settings.'),
                            }),
                            z.object({
                              weeklyOptions: z.never().optional(),
                              oneTimeOptions: z
                                .object({
                                  startDate: z
                                    .string()
                                    .describe('Start date.')
                                    .optional()
                                    .nullable(),
                                  startTime: z
                                    .string()
                                    .describe(
                                      'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                    )
                                    .max(5)
                                    .min(5)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('1-time event settings.'),
                            }),
                          ])
                        )
                      )
                      .max(10)
                      .optional(),
                  })
                  .describe('Business schedule.')
                  .optional(),
              })
              .describe(
                'Settings for this experience that are used to determine restaurant availability for reservations made online.'
              )
              .optional(),
            reservationForm: z
              .object({
                submitMessage: z
                  .string()
                  .describe(
                    'Message displayed to customers when they submit the reservation form.'
                  )
                  .max(1000000)
                  .optional()
                  .nullable(),
                policiesEnabled: z
                  .boolean()
                  .describe(
                    'Whether to display privacy policy and terms and conditions to customers.'
                  )
                  .optional()
                  .nullable(),
                privacyPolicy: z
                  .intersection(
                    z.object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        url: z.never().optional(),
                        text: z.never().optional(),
                      }),
                      z.object({
                        text: z.never().optional(),
                        url: z.string().describe('Privacy policy URL.'),
                      }),
                      z.object({
                        url: z.never().optional(),
                        text: z
                          .string()
                          .describe('Privacy policy text.')
                          .max(1000000),
                      }),
                    ])
                  )
                  .describe('Privacy policy display settings.')
                  .optional(),
                termsAndConditions: z
                  .intersection(
                    z.object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        url: z.never().optional(),
                        text: z.never().optional(),
                      }),
                      z.object({
                        text: z.never().optional(),
                        url: z.string().describe('Terms and conditions URL.'),
                      }),
                      z.object({
                        url: z.never().optional(),
                        text: z
                          .string()
                          .describe('Terms and conditions text.')
                          .max(1000000),
                      }),
                    ])
                  )
                  .describe('Terms and conditions display settings.')
                  .optional(),
                customFieldDefinitions: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Custom field ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Custom field name.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                      required: z
                        .boolean()
                        .describe(
                          'Whether the custom field is required.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(1000)
                  .optional(),
                lastNameRequired: z
                  .boolean()
                  .describe(
                    'Whether a last name is required in the reservation form.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                emailRequired: z
                  .boolean()
                  .describe(
                    'Whether an email is required in the reservation form.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                emailMarketingCheckbox: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                    checkedByDefault: z
                      .boolean()
                      .describe(
                        'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Email marketing checkbox settings.')
                  .optional(),
              })
              .describe(
                'Custom form fields and settings for the reservation booking form.'
              )
              .optional(),
            visible: z
              .boolean()
              .describe('Whether the experience is visible on the live site.')
              .optional()
              .nullable(),
          })
          .describe('Experience configuration.')
          .optional(),
        currency: z
          .string()
          .describe(
            '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
          )
          .optional(),
        slug: z
          .string()
          .describe(
            'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
          )
          .max(300)
          .optional()
          .nullable(),
        seoSettings: z
          .object({
            seoSchema: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe(
                'SEO schema data for customizing meta tags and search engine settings.'
              )
              .optional(),
          })
          .describe('SEO settings for the experience page.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Cursor paging metadata.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe('Aggregation name defined in the request.')
                .max(1000)
                .optional(),
              type: z
                .enum([
                  'UNKNOWN_AGGREGATION_TYPE',
                  'VALUE',
                  'RANGE',
                  'SCALAR',
                  'DATE_HISTOGRAM',
                  'NESTED',
                ])
                .describe('Type of aggregation that was performed.')
                .optional(),
              fieldPath: z
                .string()
                .describe('Field the data was aggregated by.')
                .max(1000)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field')
                            .max(1000)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities with this value')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities in the range.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum([
                        'UNKNOWN_SCALAR_TYPE',
                        'COUNT_DISTINCT',
                        'MIN',
                        'MAX',
                      ])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z.string().max(1000).optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'Aggregation name defined in the request.'
                                  )
                                  .max(1000)
                                  .optional(),
                                type: z
                                  .enum([
                                    'UNKNOWN_AGGREGATION_TYPE',
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe('Type of aggregation performed.')
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe('Field the data was aggregated by.')
                                  .max(1000)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field')
                                              .max(1000)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities with this value'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(1000)
                                        .optional(),
                                    })
                                    .describe(
                                      'List of the value aggregation results.'
                                    ),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities in the range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(1000)
                                        .optional(),
                                    })
                                    .describe(
                                      'List of the range aggregation results.'
                                    ),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum([
                                          'UNKNOWN_SCALAR_TYPE',
                                          'COUNT_DISTINCT',
                                          'MIN',
                                          'MAX',
                                        ])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'List of the scalar aggregation results.'
                                    ),
                                }),
                              ])
                            )
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('deprecated'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Date in ISO 8601 format.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities in the interval.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation results.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities in the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation results.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Scalar aggregation results.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation results.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation results.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('List of nested aggregation results.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
    })
    .describe('Aggregation data.')
    .optional(),
});
export const BulkUpdateExperiencesRequest = z.object({
  experiences: z
    .array(
      z.object({
        experience: z
          .object({
            _id: z
              .string()
              .describe('Experience ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            reservationLocationId: z
              .string()
              .describe(
                'ID of the reservation location this experience is assigned to.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
              ),
            _createdDate: z
              .date()
              .describe('Date and time the experience was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the experience was last updated.')
              .optional()
              .nullable(),
            configuration: z
              .object({
                displayInfo: z
                  .object({
                    name: z
                      .string()
                      .describe('Experience name.')
                      .min(1)
                      .max(120)
                      .optional()
                      .nullable(),
                    shortDescription: z
                      .string()
                      .describe('Brief description of the experience.')
                      .max(300)
                      .optional()
                      .nullable(),
                    coverImage: z
                      .string()
                      .describe('Cover image for the experience.')
                      .optional(),
                    richContent: z
                      .any()
                      .describe(
                        'Detailed description of the experience using rich content formatting.'
                      )
                      .optional(),
                  })
                  .describe('Display information for the experience.')
                  .optional(),
                paymentPolicy: z
                  .intersection(
                    z.object({
                      paymentPolicyType: z
                        .enum(['PER_GUEST', 'FREE'])
                        .optional(),
                    }),
                    z.xor([
                      z.object({ perGuestOptions: z.never().optional() }),
                      z.object({
                        perGuestOptions: z
                          .object({
                            price: z
                              .string()
                              .describe(
                                'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Per-guest pricing settings.'),
                      }),
                    ])
                  )
                  .describe(
                    'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
                  )
                  .optional(),
                onlineReservations: z
                  .object({
                    partySize: z
                      .object({
                        min: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of guests allowed for a reservation.'
                          )
                          .min(1)
                          .max(10000)
                          .optional()
                          .nullable(),
                        max: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of guests allowed for a reservation.'
                          )
                          .min(1)
                          .max(10000)
                          .optional()
                          .nullable(),
                      })
                      .describe('Party size.')
                      .optional(),
                    minimumReservationNotice: z
                      .object({
                        number: z
                          .number()
                          .int()
                          .describe(
                            'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        unit: z.enum(['MINUTES', 'HOURS', 'DAYS']).optional(),
                      })
                      .describe('Minimum reservation notice.')
                      .optional(),
                    maximumReservationNotice: z
                      .object({
                        number: z
                          .number()
                          .int()
                          .describe(
                            'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        unit: z.enum(['MINUTES', 'HOURS', 'DAYS']).optional(),
                      })
                      .describe('Maximum reservation notice.')
                      .optional(),
                    approval: z
                      .object({
                        mode: z.enum(['AUTOMATIC', 'MANUAL']).optional(),
                      })
                      .describe('Approval.')
                      .optional(),
                    maxGuests: z
                      .object({
                        number: z
                          .number()
                          .int()
                          .describe(
                            'Maximum total number of guests that can have active reservations for this experience at the same time.'
                          )
                          .min(0)
                          .max(10000)
                          .optional()
                          .nullable(),
                      })
                      .describe('Maximum number of guests.')
                      .optional(),
                    businessSchedule: z
                      .object({
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe('Duration of the experience in minutes.')
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        entries: z
                          .array(
                            z.intersection(
                              z.object({
                                recurrence: z
                                  .enum(['WEEKLY', 'ONE_TIME'])
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  weeklyOptions: z.never().optional(),
                                  oneTimeOptions: z.never().optional(),
                                }),
                                z.object({
                                  oneTimeOptions: z.never().optional(),
                                  weeklyOptions: z
                                    .object({
                                      startDate: z
                                        .string()
                                        .describe('Start date.')
                                        .optional()
                                        .nullable(),
                                      endDate: z
                                        .string()
                                        .describe('End date.')
                                        .optional()
                                        .nullable(),
                                      startDaysAndTimes: z
                                        .array(
                                          z.object({
                                            day: z
                                              .enum([
                                                'MONDAY',
                                                'TUESDAY',
                                                'WEDNESDAY',
                                                'THURSDAY',
                                                'FRIDAY',
                                                'SATURDAY',
                                                'SUNDAY',
                                              ])
                                              .optional(),
                                            time: z
                                              .string()
                                              .describe(
                                                'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                              )
                                              .max(5)
                                              .min(5)
                                              .optional()
                                              .nullable(),
                                          })
                                        )
                                        .max(100)
                                        .optional(),
                                    })
                                    .describe('Weekly recurrence settings.'),
                                }),
                                z.object({
                                  weeklyOptions: z.never().optional(),
                                  oneTimeOptions: z
                                    .object({
                                      startDate: z
                                        .string()
                                        .describe('Start date.')
                                        .optional()
                                        .nullable(),
                                      startTime: z
                                        .string()
                                        .describe(
                                          'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                        )
                                        .max(5)
                                        .min(5)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('1-time event settings.'),
                                }),
                              ])
                            )
                          )
                          .max(10)
                          .optional(),
                      })
                      .describe('Business schedule.')
                      .optional(),
                  })
                  .describe(
                    'Settings for this experience that are used to determine restaurant availability for reservations made online.'
                  )
                  .optional(),
                reservationForm: z
                  .object({
                    submitMessage: z
                      .string()
                      .describe(
                        'Message displayed to customers when they submit the reservation form.'
                      )
                      .max(1000000)
                      .optional()
                      .nullable(),
                    policiesEnabled: z
                      .boolean()
                      .describe(
                        'Whether to display privacy policy and terms and conditions to customers.'
                      )
                      .optional()
                      .nullable(),
                    privacyPolicy: z
                      .intersection(
                        z.object({
                          enabled: z
                            .boolean()
                            .describe(
                              'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                            )
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({
                            url: z.never().optional(),
                            text: z.never().optional(),
                          }),
                          z.object({
                            text: z.never().optional(),
                            url: z.string().describe('Privacy policy URL.'),
                          }),
                          z.object({
                            url: z.never().optional(),
                            text: z
                              .string()
                              .describe('Privacy policy text.')
                              .max(1000000),
                          }),
                        ])
                      )
                      .describe('Privacy policy display settings.')
                      .optional(),
                    termsAndConditions: z
                      .intersection(
                        z.object({
                          enabled: z
                            .boolean()
                            .describe(
                              'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                            )
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({
                            url: z.never().optional(),
                            text: z.never().optional(),
                          }),
                          z.object({
                            text: z.never().optional(),
                            url: z
                              .string()
                              .describe('Terms and conditions URL.'),
                          }),
                          z.object({
                            url: z.never().optional(),
                            text: z
                              .string()
                              .describe('Terms and conditions text.')
                              .max(1000000),
                          }),
                        ])
                      )
                      .describe('Terms and conditions display settings.')
                      .optional(),
                    customFieldDefinitions: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Custom field ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Custom field name.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          required: z
                            .boolean()
                            .describe(
                              'Whether the custom field is required.\n\nDefault: `false`'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(1000)
                      .optional(),
                    lastNameRequired: z
                      .boolean()
                      .describe(
                        'Whether a last name is required in the reservation form.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                    emailRequired: z
                      .boolean()
                      .describe(
                        'Whether an email is required in the reservation form.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                    emailMarketingCheckbox: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                          )
                          .optional()
                          .nullable(),
                        checkedByDefault: z
                          .boolean()
                          .describe(
                            'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Email marketing checkbox settings.')
                      .optional(),
                  })
                  .describe(
                    'Custom form fields and settings for the reservation booking form.'
                  )
                  .optional(),
                visible: z
                  .boolean()
                  .describe(
                    'Whether the experience is visible on the live site.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Experience configuration.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                publicTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
              )
              .optional(),
            slug: z
              .string()
              .describe(
                'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
              )
              .max(300)
              .optional()
              .nullable(),
            seoSettings: z
              .object({
                seoSchema: z
                  .object({
                    tags: z
                      .array(
                        z.object({
                          type: z
                            .string()
                            .describe(
                              'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                            )
                            .optional(),
                          props: z
                            .record(z.string(), z.any())
                            .describe(
                              'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                            )
                            .optional()
                            .nullable(),
                          meta: z
                            .record(z.string(), z.any())
                            .describe(
                              'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                            )
                            .optional()
                            .nullable(),
                          children: z
                            .string()
                            .describe(
                              'SEO tag inner content. For example, `<title> inner content </title>`.'
                            )
                            .optional(),
                          custom: z
                            .boolean()
                            .describe(
                              'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                            )
                            .optional(),
                          disabled: z
                            .boolean()
                            .describe(
                              "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    settings: z
                      .object({
                        preventAutoRedirect: z
                          .boolean()
                          .describe(
                            'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                          )
                          .optional(),
                        keywords: z
                          .array(
                            z.object({
                              term: z
                                .string()
                                .describe('Keyword value.')
                                .optional(),
                              isMain: z
                                .boolean()
                                .describe(
                                  'Whether the keyword is the main focus keyword.'
                                )
                                .optional(),
                              origin: z
                                .string()
                                .describe(
                                  'The source that added the keyword terms to the SEO settings.'
                                )
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                          )
                          .max(5)
                          .optional(),
                      })
                      .describe('SEO general settings.')
                      .optional(),
                  })
                  .describe(
                    'SEO schema data for customizing meta tags and search engine settings.'
                  )
                  .optional(),
              })
              .describe('SEO settings for the experience page.')
              .optional(),
          })
          .describe('Experience to update. May be partial.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          "Whether to include the updated experiences in the response. When `true`, each successfully updated experience is returned in the corresponding result's `item` field.\n\nDefault: `false`"
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateExperiencesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Update status for an individual experience, including success or failure information and error details when the update failed.'
          )
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Experience ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            reservationLocationId: z
              .string()
              .describe(
                'ID of the reservation location this experience is assigned to.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the experience is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the experience.\n\nIgnored when creating an experience.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the experience was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the experience was last updated.')
              .optional()
              .nullable(),
            configuration: z
              .object({
                displayInfo: z
                  .object({
                    name: z
                      .string()
                      .describe('Experience name.')
                      .min(1)
                      .max(120)
                      .optional()
                      .nullable(),
                    shortDescription: z
                      .string()
                      .describe('Brief description of the experience.')
                      .max(300)
                      .optional()
                      .nullable(),
                    coverImage: z
                      .string()
                      .describe('Cover image for the experience.')
                      .optional(),
                    richContent: z
                      .any()
                      .describe(
                        'Detailed description of the experience using rich content formatting.'
                      )
                      .optional(),
                  })
                  .describe('Display information for the experience.')
                  .optional(),
                paymentPolicy: z
                  .intersection(
                    z.object({
                      paymentPolicyType: z
                        .enum(['PER_GUEST', 'FREE'])
                        .describe('Payment policy type for the experience.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({ perGuestOptions: z.never().optional() }),
                      z.object({
                        perGuestOptions: z
                          .object({
                            price: z
                              .string()
                              .describe(
                                'Monetary amount to charge. Decimal string with a period as a decimal separator. For example, `"3.99"`.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Per-guest pricing settings.'),
                      }),
                    ])
                  )
                  .describe(
                    'Payment policy for the experience. This policy determines how the restaurant charges for reservations.'
                  )
                  .optional(),
                onlineReservations: z
                  .object({
                    partySize: z
                      .object({
                        min: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of guests allowed for a reservation.'
                          )
                          .min(1)
                          .max(10000)
                          .optional()
                          .nullable(),
                        max: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of guests allowed for a reservation.'
                          )
                          .min(1)
                          .max(10000)
                          .optional()
                          .nullable(),
                      })
                      .describe('Party size.')
                      .optional(),
                    minimumReservationNotice: z
                      .object({
                        number: z
                          .number()
                          .int()
                          .describe(
                            'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['MINUTES', 'HOURS', 'DAYS'])
                          .describe('Time unit for the notice period.')
                          .optional(),
                      })
                      .describe('Minimum reservation notice.')
                      .optional(),
                    maximumReservationNotice: z
                      .object({
                        number: z
                          .number()
                          .int()
                          .describe(
                            'Number of time units for the notice period. For example, `2` for 2 hours or `30` for 30 days.'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['MINUTES', 'HOURS', 'DAYS'])
                          .describe('Time unit for the notice period.')
                          .optional(),
                      })
                      .describe('Maximum reservation notice.')
                      .optional(),
                    approval: z
                      .object({
                        mode: z
                          .enum(['AUTOMATIC', 'MANUAL'])
                          .describe('Approval mode.')
                          .optional(),
                      })
                      .describe('Approval.')
                      .optional(),
                    maxGuests: z
                      .object({
                        number: z
                          .number()
                          .int()
                          .describe(
                            'Maximum total number of guests that can have active reservations for this experience at the same time.'
                          )
                          .min(0)
                          .max(10000)
                          .optional()
                          .nullable(),
                      })
                      .describe('Maximum number of guests.')
                      .optional(),
                    businessSchedule: z
                      .object({
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe('Duration of the experience in minutes.')
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        entries: z
                          .array(
                            z.intersection(
                              z.object({
                                recurrence: z
                                  .enum(['WEEKLY', 'ONE_TIME'])
                                  .describe(
                                    'Recurrence settings for this entry.'
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  weeklyOptions: z.never().optional(),
                                  oneTimeOptions: z.never().optional(),
                                }),
                                z.object({
                                  oneTimeOptions: z.never().optional(),
                                  weeklyOptions: z
                                    .object({
                                      startDate: z
                                        .string()
                                        .describe('Start date.')
                                        .optional()
                                        .nullable(),
                                      endDate: z
                                        .string()
                                        .describe('End date.')
                                        .optional()
                                        .nullable(),
                                      startDaysAndTimes: z
                                        .array(
                                          z.object({
                                            day: z
                                              .enum([
                                                'MONDAY',
                                                'TUESDAY',
                                                'WEDNESDAY',
                                                'THURSDAY',
                                                'FRIDAY',
                                                'SATURDAY',
                                                'SUNDAY',
                                              ])
                                              .describe('Day of week.')
                                              .optional(),
                                            time: z
                                              .string()
                                              .describe(
                                                'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                              )
                                              .max(5)
                                              .min(5)
                                              .optional()
                                              .nullable(),
                                          })
                                        )
                                        .max(100)
                                        .optional(),
                                    })
                                    .describe('Weekly recurrence settings.'),
                                }),
                                z.object({
                                  weeklyOptions: z.never().optional(),
                                  oneTimeOptions: z
                                    .object({
                                      startDate: z
                                        .string()
                                        .describe('Start date.')
                                        .optional()
                                        .nullable(),
                                      startTime: z
                                        .string()
                                        .describe(
                                          'Start time. ISO-8601 extended local time format (hh:mm). For example, `"11:00"`, or `"23:30"`.'
                                        )
                                        .max(5)
                                        .min(5)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('1-time event settings.'),
                                }),
                              ])
                            )
                          )
                          .max(10)
                          .optional(),
                      })
                      .describe('Business schedule.')
                      .optional(),
                  })
                  .describe(
                    'Settings for this experience that are used to determine restaurant availability for reservations made online.'
                  )
                  .optional(),
                reservationForm: z
                  .object({
                    submitMessage: z
                      .string()
                      .describe(
                        'Message displayed to customers when they submit the reservation form.'
                      )
                      .max(1000000)
                      .optional()
                      .nullable(),
                    policiesEnabled: z
                      .boolean()
                      .describe(
                        'Whether to display privacy policy and terms and conditions to customers.'
                      )
                      .optional()
                      .nullable(),
                    privacyPolicy: z
                      .intersection(
                        z.object({
                          enabled: z
                            .boolean()
                            .describe(
                              'Whether the privacy policy is displayed to customers.\n\nDefault: `false`'
                            )
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({
                            url: z.never().optional(),
                            text: z.never().optional(),
                          }),
                          z.object({
                            text: z.never().optional(),
                            url: z.string().describe('Privacy policy URL.'),
                          }),
                          z.object({
                            url: z.never().optional(),
                            text: z
                              .string()
                              .describe('Privacy policy text.')
                              .max(1000000),
                          }),
                        ])
                      )
                      .describe('Privacy policy display settings.')
                      .optional(),
                    termsAndConditions: z
                      .intersection(
                        z.object({
                          enabled: z
                            .boolean()
                            .describe(
                              'Whether the terms and conditions are displayed to customers.\n\nDefault: `false`'
                            )
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({
                            url: z.never().optional(),
                            text: z.never().optional(),
                          }),
                          z.object({
                            text: z.never().optional(),
                            url: z
                              .string()
                              .describe('Terms and conditions URL.'),
                          }),
                          z.object({
                            url: z.never().optional(),
                            text: z
                              .string()
                              .describe('Terms and conditions text.')
                              .max(1000000),
                          }),
                        ])
                      )
                      .describe('Terms and conditions display settings.')
                      .optional(),
                    customFieldDefinitions: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Custom field ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Custom field name.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          required: z
                            .boolean()
                            .describe(
                              'Whether the custom field is required.\n\nDefault: `false`'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(1000)
                      .optional(),
                    lastNameRequired: z
                      .boolean()
                      .describe(
                        'Whether a last name is required in the reservation form.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                    emailRequired: z
                      .boolean()
                      .describe(
                        'Whether an email is required in the reservation form.\n\nDefault: `false`'
                      )
                      .optional()
                      .nullable(),
                    emailMarketingCheckbox: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether the email marketing checkbox is displayed to customers.\n\nDefault: `false`'
                          )
                          .optional()
                          .nullable(),
                        checkedByDefault: z
                          .boolean()
                          .describe(
                            'Whether the email marketing checkbox is checked by default.\n\nDefault: `false`'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Email marketing checkbox settings.')
                      .optional(),
                  })
                  .describe(
                    'Custom form fields and settings for the reservation booking form.'
                  )
                  .optional(),
                visible: z
                  .boolean()
                  .describe(
                    'Whether the experience is visible on the live site.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Experience configuration.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in ISO-4217 alphabetic format. For exampled, `USD`.'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                publicTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags assigned to the experience. Tags must be created using the [Tags API](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) before they can be assigned.'
              )
              .optional(),
            slug: z
              .string()
              .describe(
                'URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format (e.g., "wine-tasting-event"). Used to create readable URLs for the experience page.'
              )
              .max(300)
              .optional()
              .nullable(),
            seoSettings: z
              .object({
                seoSchema: z
                  .object({
                    tags: z
                      .array(
                        z.object({
                          type: z
                            .string()
                            .describe(
                              'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                            )
                            .optional(),
                          props: z
                            .record(z.string(), z.any())
                            .describe(
                              'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                            )
                            .optional()
                            .nullable(),
                          meta: z
                            .record(z.string(), z.any())
                            .describe(
                              'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                            )
                            .optional()
                            .nullable(),
                          children: z
                            .string()
                            .describe(
                              'SEO tag inner content. For example, `<title> inner content </title>`.'
                            )
                            .optional(),
                          custom: z
                            .boolean()
                            .describe(
                              'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                            )
                            .optional(),
                          disabled: z
                            .boolean()
                            .describe(
                              "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    settings: z
                      .object({
                        preventAutoRedirect: z
                          .boolean()
                          .describe(
                            'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                          )
                          .optional(),
                        keywords: z
                          .array(
                            z.object({
                              term: z
                                .string()
                                .describe('Keyword value.')
                                .optional(),
                              isMain: z
                                .boolean()
                                .describe(
                                  'Whether the keyword is the main focus keyword.'
                                )
                                .optional(),
                              origin: z
                                .string()
                                .describe(
                                  'The source that added the keyword terms to the SEO settings.'
                                )
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                          )
                          .max(5)
                          .optional(),
                      })
                      .describe('SEO general settings.')
                      .optional(),
                  })
                  .describe(
                    'SEO schema data for customizing meta tags and search engine settings.'
                  )
                  .optional(),
              })
              .describe('SEO settings for the experience page.')
              .optional(),
          })
          .describe(
            'Updated experience.\n\nReturned only when `returnEntity` is `true` in the request and the update succeeded.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe(
      'Metadata for the bulk update, including the total number of successful and failed updates.'
    )
    .optional(),
});
export const BulkUpdateExperienceTagsRequest = z.object({
  experienceIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the experiences.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the experiences.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateExperienceTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Metadata about the individual experience tag update operation.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Metadata about the bulk update operation.')
    .optional(),
});
export const BulkUpdateExperienceTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe('Filter criteria to select experiences for tag updates.'),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the filtered experiences.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the filtered experiences.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateExperienceTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe('Job ID for tracking the asynchronous bulk update operation.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkArchiveExperiencesRequest = z.object({
  experienceIds: z.array(z.string()).min(1).max(100),
});
export const BulkArchiveExperiencesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata about archiving the experience.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUnarchiveExperiencesRequest = z.object({
  experienceIds: z.array(z.string()).min(1).max(100),
});
export const BulkUnarchiveExperiencesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata about unarchiving the experience.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
